import React, { useState, useEffect, useContext } from "react";
import apis from "../../services/api";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaTimes,
  FaSave,
  FaSpinner,
  FaEye,
  FaDownload,
  FaSignOutAlt,
} from "react-icons/fa";
import Header from "../Common/Header";
import Particles from "../Common/Particles";
import { ToastContext } from "../../contexts/ToastContext";

// Map user type strings to integers and vice versa
const userTypeMap = {
  Free: 0,
  Premium: 1,
  VIP: 2,
};

const userTypeReverseMap = {
  0: "Free",
  1: "Premium",
  2: "VIP",
};

export default function AdminPanel() {
  const { addToast } = useContext(ToastContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // ---------- NEW: role filter state ----------
  const [roleFilter, setRoleFilter] = useState("All");

  // ---------- Count how many Admins, Users, Guests ----------
  const adminCount = users.filter((u) => u.role === "Admin").length;
  const userCount = users.filter((u) => u.role === "User").length;
  const guestCount = users.filter((u) => u.role === "Guest").length;

  // ---------- Computed array of displayed users ----------
  const displayedUsers =
    roleFilter === "All"
      ? users
      : users.filter((user) => user.role === roleFilter);

  // Selected user details and modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Create modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: "",
    email: "",
    password: "",
    isActive: true,
    validFrom: "",
    validTo: "",
    role: "User",
    type: "Free",
  });

  // Update modal states
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    email: "",
    password: "",
    isActive: true,
    validFrom: "",
    validTo: "",
    role: "User",
    type: "Free",
  });

  // Fetch all users from the backend
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apis.Security.fetchAllUsers();
      setUsers(response);
    } catch (err) {
      addToast("Failed to fetch users.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch detail of a single user
  const fetchUserDetails = async (username) => {
    try {
      const response = await apis.Security.fetchUser(username);
      setDetailUser(response);
      setShowDetailModal(true);
    } catch (err) {
      addToast("Failed to fetch user details.", "error");
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle creating a new user
  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (createForm.username.length < 3 || createForm.username.length > 50) {
      addToast("Username must be between 3 and 50 characters.", "error");
      return;
    }

    if (createForm.password.length < 6 || createForm.password.length > 50) {
      addToast("Password must be between 6 and 50 characters.", "error");
      return;
    }

    if (!["Admin", "User"].includes(createForm.role)) {
      addToast("Role must be either Admin or User.", "error");
      return;
    }

    // Convert user type string to int if role is User
    const typeValue =
      createForm.role === "User" ? userTypeMap[createForm.type] : null;

    setLoadingCreate(true);
    try {
      const payload = {
        username: createForm.username,
        email: createForm.email,
        password: createForm.password,
        isActive: createForm.isActive,
        validFrom: createForm.validFrom || null,
        validTo: createForm.validTo || null,
        role: createForm.role,
        type: typeValue,
      };

      const response = await apis.Security.createUser(payload);
      if (response.message === "User created successfully.") {
        addToast("User created successfully.", "success");
        setCreateForm({
          username: "",
          email: "",
          password: "",
          isActive: true,
          validFrom: "",
          validTo: "",
          role: "User",
          type: "Free",
        });
        setShowCreateModal(false);
        fetchUsers();
      } else {
        addToast(response.message || "Failed to create user.", "error");
      }
    } catch (err) {
      addToast("Failed to create user.", "error");
    } finally {
      setLoadingCreate(false);
    }
  };

  // Handle updating an existing user
  const handleUpdateUser = async (e) => {
    e.preventDefault();

    if (
      updateForm.password &&
      (updateForm.password.length < 6 || updateForm.password.length > 50)
    ) {
      addToast("Password must be between 6 and 50 characters.", "error");
      return;
    }

    if (!["Admin", "User", "Guest"].includes(updateForm.role)) {
      addToast("Role must be Admin, User, or Guest.", "error");
      return;
    }

    const typeValue =
      updateForm.role === "User" ? userTypeMap[updateForm.type] : null;

    setLoadingUpdate(true);
    try {
      const payload = {
        email: updateForm.email || undefined,
        password: updateForm.password || undefined,
        isActive: updateForm.isActive,
        validFrom: updateForm.validFrom || null,
        validTo: updateForm.validTo || null,
        role: updateForm.role,
        type: typeValue,
      };

      const response = await apis.Security.updateUser(
        selectedUser.username,
        payload
      );
      if (response.message === "User updated successfully.") {
        addToast("User updated successfully.", "success");
        setSelectedUser(null);
        setUpdateForm({
          email: "",
          password: "",
          isActive: true,
          validFrom: "",
          validTo: "",
          role: "User",
          type: "Free",
        });
        setShowUpdateModal(false);
        setShowDetailModal(false);
        fetchUsers();
      } else {
        addToast(response.message || "Failed to update user.", "error");
      }
    } catch (err) {
      addToast("Failed to update user.", "error");
    } finally {
      setLoadingUpdate(false);
    }
  };

  // Handle deleting a user
  const handleDeleteUser = async (username) => {
    if (window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      try {
        const response = await apis.Security.deleteUser(username);
        if (response.message === "User deleted successfully.") {
          addToast("User deleted successfully.", "success");
          setShowDetailModal(false);
          fetchUsers();
        } else {
          addToast(response.message || "Failed to delete user.", "error");
        }
      } catch (err) {
        addToast("Failed to delete user.", "error");
      }
    }
  };

  // Open the update modal with the selected user's details
  const openUpdateModal = (user) => {
    setSelectedUser(user);
    setUpdateForm({
      email: user.email || "",
      password: "",
      isActive: user.isActive,
      validFrom: user.validFrom ? user.validFrom.split("T")[0] : "",
      validTo: user.validTo ? user.validTo.split("T")[0] : "",
      role: user.role,
      type:
        user.role === "User"
          ? userTypeReverseMap[
              typeof user.userType === "string"
                ? userTypeMap[user.userType]
                : userTypeMap["Free"]
            ] || "Free"
          : "Free",
    });
    setShowUpdateModal(true);
  };

  // Backup download handler
  const handleDownloadBackup = async () => {
    try {
      await apis.Security.downloadUsersBackup();
      addToast("Backup downloaded successfully!", "success");
    } catch (err) {
      addToast("Failed to download backup.", "error");
    }
  };

  // Admin Logout all sessions handler
  const handleAdminLogoutAllSessions = async (username) => {
    if (
      window.confirm(`Are you sure you want to logout all sessions for "${username}"?`)
    ) {
      try {
        const response = await apis.Security.adminLogoutAllSessions(username);
        addToast(
          response.message || "All sessions have been removed successfully.",
          "success"
        );

        // Update the detailUser sessions if currently displayed
        if (detailUser && detailUser.username === username) {
          setDetailUser({ ...detailUser, sessions: [] });
        }
      } catch (err) {
        addToast("Failed to remove all sessions for this user.", "error");
      }
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen flex flex-col items-center justify-start pt-20 pb-10 px-4 bg-gradient-to-b from-primary to-secondary relative overflow-hidden">
        <Particles count={40} />

        <h1 className="text-4xl font-bold mb-6 text-accentBlue">Admin Panel</h1>

        {loading && (
          <div className="flex items-center space-x-2 bg-blue-500 text-white p-4 rounded mb-4 w-full max-w-4xl animate-pulse">
            <FaSpinner className="animate-spin h-6 w-6" />
            <span>Loading users...</span>
          </div>
        )}

        {/* FILTER & COUNTS ROW */}
        <div className="flex flex-col w-full max-w-4xl mb-4 space-y-3 md:flex-row md:space-y-0 md:justify-between md:items-center">
          {/* Left side: filter dropdown */}
          <div className="flex flex-col">
            <label htmlFor="roleFilter" className="text-gray-300 mb-1">
              Filter by Role:
            </label>
            <select
              id="roleFilter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="bg-gray-700 text-gray-200 p-2 rounded focus:outline-none focus:ring-2 focus:ring-accentBlue"
            >
              <option value="All">All</option>
              <option value="Admin">Admin</option>
              <option value="User">User</option>
              <option value="Guest">Guest</option>
            </select>
          </div>

          {/* Right side: quick role counts */}
          <div className="flex flex-wrap gap-4 text-sm text-gray-300 mt-2 md:mt-0">
            <div>Total: {users.length}</div>
            <div>Admins: {adminCount}</div>
            <div>Users: {userCount}</div>
            <div>Guests: {guestCount}</div>
          </div>
        </div>

        {/* CREATE USER BUTTON */}
        <div className="flex justify-end mb-4 w-full max-w-4xl">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-green-500 text-white px-4 py-2 rounded flex items-center hover:bg-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-400"
            aria-label="Create User"
          >
            <FaPlus className="mr-2" />
            Create User
          </button>
        </div>

        {/* USERS TABLE */}
        <div className="overflow-x-auto w-full max-w-4xl">
          <table className="min-w-full bg-glassOverlay backdrop-blur-md text-gray-200 rounded-lg overflow-hidden">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b border-gray-700 text-center">
                  Username
                </th>
                <th className="py-2 px-4 border-b border-gray-700 text-center">
                  Email
                </th>
                <th className="py-2 px-4 border-b border-gray-700 text-center">
                  Role
                </th>
                <th className="py-2 px-4 border-b border-gray-700 text-center">
                  Active
                </th>
                <th className="py-2 px-4 border-b border-gray-700 text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.length > 0 ? (
                displayedUsers.map((user) => (
                  <tr
                    key={user.username}
                    className="hover:bg-gray-700 transition-colors"
                  >
                    <td className="py-2 px-4 border-b border-gray-700 text-center">
                      {user.username}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-700 text-center">
                      {user.email || "N/A"}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-700 text-center">
                      {user.role}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-700 text-center">
                      {user.isActive ? "✅" : "❌"}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-700 flex justify-center space-x-2">
                      <button
                        onClick={() => fetchUserDetails(user.username)}
                        className="bg-blue-500 text-white px-3 py-1 rounded flex items-center hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
                        aria-label={`View ${user.username}`}
                      >
                        <FaEye className="mr-1" /> View
                      </button>
                    </td>
                  </tr>
                ))
              ) : !loading ? (
                <tr>
                  <td colSpan="5" className="py-4 px-4 text-center">
                    No users found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>

          {/* Download Backup button */}
          <div className="mt-4 flex justify-center w-full max-w-4xl">
            <button
              onClick={handleDownloadBackup}
              className="bg-blue-500 text-white px-4 py-2 rounded flex items-center hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
              aria-label="Download Backup"
            >
              <FaDownload className="mr-2" /> Backup
            </button>
          </div>
        </div>

        {/* DETAIL MODAL */}
        {showDetailModal && detailUser && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="detail-user-title"
          >
            <div className="bg-glassOverlay backdrop-blur-md p-6 rounded-lg w-11/12 max-w-lg animate-zoomIn">
              <div className="flex justify-between items-center mb-4">
                <h2
                  id="detail-user-title"
                  className="text-2xl font-semibold text-gray-200"
                >
                  User Details: {detailUser.username}
                </h2>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setDetailUser(null);
                  }}
                  aria-label="Close Detail Modal"
                >
                  <FaTimes className="text-gray-400 hover:text-gray-200" />
                </button>
              </div>
              <div className="space-y-2 text-gray-300">
                <p>
                  <strong>Email:</strong> {detailUser.email || "N/A"}
                </p>
                <p>
                  <strong>Active:</strong> {detailUser.isActive ? "Yes" : "No"}
                </p>
                <p>
                  <strong>Role:</strong> {detailUser.role}
                </p>
                {detailUser.role === "User" && (
                  <p>
                    <strong>Type:</strong> {detailUser.userType || "Free"}
                  </p>
                )}
                <p>
                  <strong>Valid From:</strong>{" "}
                  {detailUser.validFrom
                    ? new Date(detailUser.validFrom).toLocaleDateString()
                    : "N/A"}
                </p>
                <p>
                  <strong>Valid To:</strong>{" "}
                  {detailUser.validTo
                    ? new Date(detailUser.validTo).toLocaleDateString()
                    : "N/A"}
                </p>
                <p>
                  <strong>Registered Date:</strong>{" "}
                  {detailUser.registeredDate
                    ? new Date(detailUser.registeredDate).toLocaleDateString()
                    : "N/A"}
                </p>
                <p>
                  <strong>API Call Count:</strong> {detailUser.apiCallCount}
                </p>
                <p>
                  <strong>Last API Call:</strong>{" "}
                  {detailUser.lastApiCall
                    ? new Date(detailUser.lastApiCall).toLocaleString()
                    : "N/A"}
                </p>
                <p className="font-semibold">Sessions:</p>
                {detailUser.sessions && detailUser.sessions.length > 0 ? (
                  <div className="max-h-32 overflow-auto border border-gray-600 rounded p-2">
                    {detailUser.sessions.map((session) => (
                      <div
                        key={session.sessionId}
                        className="border-b border-gray-600 py-1"
                      >
                        <p>
                          <strong>Session ID:</strong> {session.sessionId}
                        </p>
                        <p>
                          <strong>Last Active:</strong>{" "}
                          {new Date(session.lastActive).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No active sessions.</p>
                )}
              </div>

              <div className="flex justify-end space-x-2 mt-4">
                <button
                  onClick={() => handleDeleteUser(detailUser.username)}
                  className="flex items-center bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
                  aria-label="Delete User"
                >
                  <FaTrash className="mr-1" />
                  <span>Delete</span>
                </button>
                <button
                  onClick={() => {
                    setSelectedUser(detailUser);
                    openUpdateModal(detailUser);
                  }}
                  className="flex items-center bg-yellow-500 text-white py-2 px-4 rounded hover:bg-yellow-600 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  aria-label="Edit User"
                >
                  <FaEdit className="mr-1" />
                  <span>Edit</span>
                </button>
                {/* Logout All Sessions Button */}
                <button
                  onClick={() => handleAdminLogoutAllSessions(detailUser.username)}
                  className="flex items-center bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-400"
                  aria-label="Logout All Sessions"
                >
                  <FaSignOutAlt className="mr-1" />
                  <span>Logout Sessions</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CREATE USER MODAL */}
        {showCreateModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-user-title"
          >
            <div className="bg-glassOverlay backdrop-blur-md p-6 rounded-lg w-11/12 max-w-md animate-zoomIn">
              <div className="flex justify-between items-center mb-4">
                <h2
                  id="create-user-title"
                  className="text-2xl font-semibold text-gray-200"
                >
                  Create New User
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  aria-label="Close Create User Modal"
                >
                  <FaTimes className="text-gray-400 hover:text-gray-200" />
                </button>
              </div>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="flex flex-col">
                  <label
                    htmlFor="create-username"
                    className="text-lg text-gray-300"
                  >
                    Username
                  </label>
                  <input
                    id="create-username"
                    type="text"
                    required
                    value={createForm.username}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, username: e.target.value })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                    placeholder="Enter username"
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="create-email"
                    className="text-lg text-gray-300"
                  >
                    Email
                  </label>
                  <input
                    id="create-email"
                    type="email"
                    required
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, email: e.target.value })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                    placeholder="Enter email"
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="create-password"
                    className="text-lg text-gray-300"
                  >
                    Password
                  </label>
                  <input
                    id="create-password"
                    type="password"
                    required
                    value={createForm.password}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, password: e.target.value })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                    placeholder="Enter password"
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="create-role"
                    className="text-lg text-gray-300"
                  >
                    Role
                  </label>
                  <select
                    id="create-role"
                    value={createForm.role}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, role: e.target.value })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                  >
                    <option value="User">User</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
                {createForm.role === "User" && (
                  <div className="flex flex-col">
                    <label
                      htmlFor="create-type"
                      className="text-lg text-gray-300"
                    >
                      User Type
                    </label>
                    <select
                      id="create-type"
                      value={createForm.type}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, type: e.target.value })
                      }
                      className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                    >
                      <option value="Free">Free</option>
                      <option value="Premium">Premium</option>
                      <option value="VIP">VIP</option>
                    </select>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <input
                    id="create-active"
                    type="checkbox"
                    checked={createForm.isActive}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        isActive: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-accentBlue focus:ring-accentBlue border-gray-600 rounded"
                  />
                  <label
                    htmlFor="create-active"
                    className="text-lg text-gray-300"
                  >
                    Active
                  </label>
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="create-validFrom"
                    className="text-lg text-gray-300"
                  >
                    Valid From
                  </label>
                  <input
                    id="create-validFrom"
                    type="date"
                    value={createForm.validFrom}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        validFrom: e.target.value,
                      })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="create-validTo"
                    className="text-lg text-gray-300"
                  >
                    Valid To
                  </label>
                  <input
                    id="create-validTo"
                    type="date"
                    value={createForm.validTo}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, validTo: e.target.value })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex items-center bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
                    aria-label="Cancel"
                  >
                    <FaTimes className="mr-1" />
                    <span>Cancel</span>
                  </button>
                  <button
                    type="submit"
                    disabled={loadingCreate}
                    className={`flex items-center bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-400 ${
                      loadingCreate ? "cursor-not-allowed opacity-70" : ""
                    }`}
                    aria-label="Create User"
                  >
                    {loadingCreate ? (
                      <>
                        <FaSpinner className="animate-spin mr-1" />
                        <span>Creating...</span>
                      </>
                    ) : (
                      <>
                        <FaSave className="mr-1" />
                        <span>Create</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* UPDATE USER MODAL */}
        {showUpdateModal && selectedUser && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="update-user-title"
          >
            <div className="bg-glassOverlay backdrop-blur-md p-6 rounded-lg w-11/12 max-w-md animate-zoomIn">
              <div className="flex justify-between items-center mb-4">
                <h2
                  id="update-user-title"
                  className="text-2xl font-semibold text-gray-200"
                >
                  Update User: {selectedUser.username}
                </h2>
                <button
                  onClick={() => setShowUpdateModal(false)}
                  aria-label="Close Update User Modal"
                >
                  <FaTimes className="text-gray-400 hover:text-gray-200" />
                </button>
              </div>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="flex flex-col">
                  <label
                    htmlFor="update-email"
                    className="text-lg text-gray-300"
                  >
                    Email
                  </label>
                  <input
                    id="update-email"
                    type="email"
                    value={updateForm.email}
                    onChange={(e) =>
                      setUpdateForm({ ...updateForm, email: e.target.value })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                    placeholder="Enter email"
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="update-password"
                    className="text-lg text-gray-300"
                  >
                    Password
                  </label>
                  <input
                    id="update-password"
                    type="password"
                    value={updateForm.password}
                    onChange={(e) =>
                      setUpdateForm({ ...updateForm, password: e.target.value })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                    placeholder="Leave blank to keep current password"
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="update-role"
                    className="text-lg text-gray-300"
                  >
                    Role
                  </label>
                  <select
                    id="update-role"
                    value={updateForm.role}
                    onChange={(e) =>
                      setUpdateForm({ ...updateForm, role: e.target.value })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                  >
                    <option value="User">User</option>
                    <option value="Admin">Admin</option>
                    <option value="Guest">Guest</option>
                  </select>
                </div>
                {updateForm.role === "User" && (
                  <div className="flex flex-col">
                    <label
                      htmlFor="update-type"
                      className="text-lg text-gray-300"
                    >
                      User Type
                    </label>
                    <select
                      id="update-type"
                      value={updateForm.type}
                      onChange={(e) =>
                        setUpdateForm({ ...updateForm, type: e.target.value })
                      }
                      className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                    >
                      <option value="Free">Free</option>
                      <option value="Premium">Premium</option>
                      <option value="VIP">VIP</option>
                    </select>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <input
                    id="update-active"
                    type="checkbox"
                    checked={updateForm.isActive}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        isActive: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-accentBlue focus:ring-accentBlue border-gray-600 rounded"
                  />
                  <label
                    htmlFor="update-active"
                    className="text-lg text-gray-300"
                  >
                    Active
                  </label>
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="update-validFrom"
                    className="text-lg text-gray-300"
                  >
                    Valid From
                  </label>
                  <input
                    id="update-validFrom"
                    type="date"
                    value={updateForm.validFrom}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        validFrom: e.target.value,
                      })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    htmlFor="update-validTo"
                    className="text-lg text-gray-300"
                  >
                    Valid To
                  </label>
                  <input
                    id="update-validTo"
                    type="date"
                    value={updateForm.validTo}
                    onChange={(e) =>
                      setUpdateForm({ ...updateForm, validTo: e.target.value })
                    }
                    className="p-2 rounded border border-gray-600 bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-accentBlue"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowUpdateModal(false)}
                    className="flex items-center bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
                    aria-label="Cancel"
                  >
                    <FaTimes className="mr-1" />
                    <span>Cancel</span>
                  </button>
                  <button
                    type="submit"
                    disabled={loadingUpdate}
                    className={`flex items-center bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                      loadingUpdate ? "cursor-not-allowed opacity-70" : ""
                    }`}
                    aria-label="Update User"
                  >
                    {loadingUpdate ? (
                      <>
                        <FaSpinner className="animate-spin mr-1" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <FaSave className="mr-1" />
                        <span>Update</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
